var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState } from 'react';
import classNames from 'classnames';
import { Slider as MuiSlider, IconButton } from '@material-ui/core';
import { makeStyles } from "../../styles/index";
import { useTheme } from "../../hooks/useTheme";
import { useMeasure } from "../../hooks/useMeasure";
import { Settings, ChevronLeft, ChevronRight } from "../../index";
// 外で定数として定義してしまう。可読性のため。
// 使う場所によって変える必要がでてきたらpropsで受け取って内部で計算して、、とする必要があるかもしれないが現時点では必要でない。
var SLIDER_THICKNESS = 8;
var THUMB_SIZE = 30;
// スライダーコンポーネントのスタイル指定とカラーテーマ適用を記述する
var styles = function (theme) { return ({
    root: {
        backgroundColor: 'transparent',
    },
    sliderRoot: {
        color: '#3d7cff',
        // スライダー本体の両端にボタンを配置するスペースを得るため、ボタンを除く部分の幅を70%にする。
        width: 'calc(100% - 54px * 2)',
        padding: 0,
        flex: '1 0 auto',
        height: SLIDER_THICKNESS,
        '& .MuiSlider-thumb': {
            height: THUMB_SIZE,
            width: THUMB_SIZE,
            marginTop: -(THUMB_SIZE / 2 + SLIDER_THICKNESS / 2),
            marginLeft: -(THUMB_SIZE / 2),
            top: '100%',
            '&:hover, &:active': {
                boxShadow: 'inherit',
                backgroundColor: '#699BFC',
                color: '#fff',
            },
        },
    },
    active: {},
    track: {
        height: SLIDER_THICKNESS,
        borderRadius: SLIDER_THICKNESS / 2,
    },
    rail: {
        height: SLIDER_THICKNESS,
        borderRadius: SLIDER_THICKNESS / 2,
    },
    '& .MuiIconButton-root:not(.Mui-disabled)': {
        flex: '0 0 auto',
        minWidth: 0,
    },
    // カラーテーマは主にスライダーのつまみ、ボタンの見た目に適用される
    light: {
        backgroundColor: theme.palette.light.background.paper,
        '& .MuiIconButton-root:not(.Mui-disabled)': {
            color: theme.palette.common.black,
            '&:hover': {
                color: theme.palette.primary.light,
                backgroundColor: 'transparent',
            },
            '&:active': {
                color: theme.palette.primary.light,
                backgroundColor: 'transparent',
            },
        },
        '& .MuiIconButton-root.Mui-disabled': {
            color: theme.palette.light.action.disabled,
        },
        '& .MuiSlider-thumb:not(.Mui-disabled)': {
            backgroundColor: theme.palette.light.background.paper,
            color: theme.palette.common.black,
        },
        '& .MuiSlider-thumb.Mui-disabled': {
            backgroundColor: theme.palette.light.action.disabled,
        },
    },
    dark: {
        backgroundColor: theme.palette.dark.background.paper,
        '& .MuiIconButton-root:not(.Mui-disabled)': {
            color: theme.palette.common.white,
            // lightと共通
            '&:hover': {
                color: theme.palette.primary.light,
                backgroundColor: 'transparent',
            },
            // lightと共通
            '&:active': {
                color: theme.palette.primary.light,
                backgroundColor: 'transparent',
            },
        },
        '& .MuiIconButton-root.Mui-disabled': {
            color: theme.palette.dark.action.disabled,
        },
        // lightと共通
        '& .MuiSlider-thumb:not(.Mui-disabled)': {
            backgroundColor: theme.palette.light.background.paper,
            color: theme.palette.common.black,
        },
        '& .MuiSlider-thumb.Mui-disabled': {
            backgroundColor: theme.palette.dark.action.disabled,
        },
    },
}); };
var useStyles = makeStyles(styles, { name: 'SnSlider' });
// つまみの見た目を用意する。
// TODO ripple effect的表示が出てしまうが無くしたい #116
// TODO 正式なつまみのデザインが未決 #116
function ThumbComponent(props) {
    return (React.createElement("span", __assign({}, props),
        React.createElement(Settings, null)));
}
// TODO 変数名・関数名の修正 #115
export var Slider = function (props) {
    var defaultValue = props.defaultValue, value = props.value, min = props.min, max = props.max, buttonStep = props.buttonStep, sliderStep = props.sliderStep, StepUpButtonComponent = props.StepUpButtonComponent, StepDownButtonComponent = props.StepDownButtonComponent, disabled = props.disabled, onChange = props.onChange, onChangeCommitted = props.onChangeCommitted, propClassName = props.className, track = props.track, style = props.style;
    var measureStyle = useMeasure(props).style;
    var classes = useStyles(props);
    var theme = useTheme(props);
    var className = classNames(classes === null || classes === void 0 ? void 0 : classes.root, classes === null || classes === void 0 ? void 0 : classes[theme.type], propClassName);
    // 型エラーを通過できるNaNが渡されときにスライダーの動作が壊れるため、警告する
    var propNotNaN = function (propsValue, propsName) {
        if (Number.isNaN(propsValue)) {
            throw new Error(propsName + "\u306B\u7121\u52B9\u306A\u5024\u304C\u6E21\u3055\u308C\u3066\u3044\u307E\u3059\u3002" + propsName + ": NaN");
        }
        return propsValue;
    };
    /**
     * プロパティが
     * undefined または null ⇒ 初期値
     * NaN ⇒ エラーを投げる
     * 数値 ⇒ 設定される
     */
    var maxValue = propNotNaN(max !== null && max !== void 0 ? max : 100, 'max');
    var minValue = propNotNaN(min !== null && min !== void 0 ? min : 0, 'min');
    var buttonStepValue = propNotNaN(buttonStep !== null && buttonStep !== void 0 ? buttonStep : 1, 'buttonStep');
    if (buttonStepValue < 0.01) {
        console.error('buttonStepは0.01未満の値をサポートしていないため、現在では加算ボタンと減算ボタンが使用不可能になります。');
    }
    // valueの初期値はminとmaxの大小関係に左右される
    var calculationInitialValue = function () {
        if (minValue > maxValue) {
            console.error("max\u3088\u308A\u3082\u5927\u304D\u3044min\u304C\u6E21\u3055\u308C\u3066\u3044\u307E\u3059\u3002value\u306B\u306Fmin\u306E\u5024\u304C\u8A2D\u5B9A\u3055\u308C\u307E\u3059\u3002");
            return minValue;
        }
        return (maxValue + minValue) / 2;
    };
    var initializeValue = propNotNaN(value !== null && value !== void 0 ? value : calculationInitialValue(), 'value');
    var _a = useState(propNotNaN(defaultValue !== null && defaultValue !== void 0 ? defaultValue : initializeValue, 'defaultValue')), sliderValue = _a[0], setValue = _a[1];
    var currentValue = value !== null && value !== void 0 ? value : sliderValue;
    if (currentValue > maxValue) {
        throw new Error("\u4E0A\u9650\u5024(max)\u3088\u308A\u3082\u5927\u304D\u3044\u5024\u304Cvalue\u306B\u6E21\u3055\u308C\u3066\u3044\u307E\u3059\u3002max=" + max + " value=" + value);
    }
    if (currentValue < minValue) {
        throw new Error("\u4E0B\u9650\u5024(min)\u3088\u308A\u3082\u5C0F\u3055\u3044\u5024\u304Cvalue\u306B\u6E21\u3055\u308C\u3066\u3044\u307E\u3059\u3002min=" + min + " value=" + value);
    }
    // valueが上限値である、またはスライダーがdisabledであるときに加算ボタンをグレーアウトにする
    // buttonStepは0.01をサポートする最小値として定める。0.01より小さい値を渡した場合にはボタンを使用不可にする。
    var disableStepUp = disabled || currentValue >= maxValue || buttonStepValue < 0.01;
    // valueが下限値である、またはスライダーがdisabledであるときに減算ボタンをグレーアウトにする
    var disableStepDown = disabled || currentValue <= minValue || buttonStepValue < 0.01;
    // Material-UIのSliderのonChangeCommittedが呼ぶとき、onChangeCommittedプロパティが受け取った処理を実行する
    // Material-UIの仕様により必要なため、newValueは配列を受け取れるようにしてある。
    // しかし今回のスライダーコンポーネントでは単一の数値しかイベントで取得しないので、newValueはas numberとして処理する。(handleSliderChangeも同様)
    var handleSliderChangeCommitted = function (event, newValue) {
        onChangeCommitted === null || onChangeCommitted === void 0 ? void 0 : onChangeCommitted(newValue);
        setValue(newValue);
    };
    // Material-UIのSliderのonChangeが呼ぶとき、onChangeプロパティが受け取った処理を実行する
    var handleSliderChange = function (event, newValue) {
        if (currentValue === maxValue && newValue === maxValue) {
            return;
        }
        if (currentValue === minValue && newValue === minValue) {
            return;
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
        setValue(newValue);
    };
    // 加算ボタン押下時の更新処理
    var handleStepUp = function () {
        // 上限値を超えないように更新する
        var newValue = Math.min(currentValue + buttonStepValue, maxValue);
        onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
        onChangeCommitted === null || onChangeCommitted === void 0 ? void 0 : onChangeCommitted(newValue);
        setValue(newValue);
    };
    // 減算ボタン押下時の更新処理（基本はhandleStepUpと同じ）
    var handleStepDown = function () {
        var newValue = Math.max(currentValue - buttonStepValue, minValue);
        onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
        onChangeCommitted === null || onChangeCommitted === void 0 ? void 0 : onChangeCommitted(newValue);
        setValue(newValue);
    };
    return (React.createElement("div", { className: className, style: __assign(__assign({}, measureStyle), style) },
        React.createElement(IconButton, { onClick: handleStepDown, disabled: disableStepDown, size: "small", disableRipple: true }, StepDownButtonComponent !== null && StepDownButtonComponent !== void 0 ? StepDownButtonComponent : React.createElement(ChevronLeft, null)),
        React.createElement(MuiSlider, { classes: { root: classes.sliderRoot, track: classes.track, rail: classes.rail }, ThumbComponent: ThumbComponent, defaultValue: defaultValue, value: currentValue, onChange: handleSliderChange, onChangeCommitted: handleSliderChangeCommitted, min: minValue, max: maxValue, step: sliderStep, disabled: disabled, track: track }),
        React.createElement(IconButton, { onClick: handleStepUp, disabled: disableStepUp, size: "small", disableRipple: true }, StepUpButtonComponent !== null && StepUpButtonComponent !== void 0 ? StepUpButtonComponent : React.createElement(ChevronRight, null))));
};
